import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useRadiobox } from "../../components/CheckRadiobox";
import {
  Button,
  ErrorMessage,
  Label,
  SplitLine,
  TextMessage,
  Title,
  StyledButton,
  TextLink
} from "../../components/generic";
import { ContainerLogin, ContainerRedirect } from "../../components/containers";
import {
  InputLabel,
  InputLabelPassword,
  useInput
} from "../../components/Input";
import { EMPTY, LOGIN_ROUTE, REGISTER_ROUTE } from "../../utils/constants";
import {
  INCORRECT_LOGIN_ERROR_MESSAGE,
  validateEmail,
  validateLength
} from "../../utils/validators";
import { useContextHospi } from "../../context/ContextHospi";
import { theme } from "styles/theme";
import { LoaderCustom } from "components/Loader";
import { clearLocalStorage } from "utils/utilAmplify";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/interfaces";
import { useHospiNavigate } from "../../utils/common";

export const Login: React.FC = () => {
  const navigate = useHospiNavigate();
  const { pathname, search } = useLocation();
  const { t, i18n } = useTranslation("login");
  const {
    isLoadingCurrentUser,
    signIn,
    logOut,
    forgotPassword,
    forgotPasswordSubmit
  } = useContextHospi();
  // Input data
  const email = useInput(process.env.REACT_APP_AWS_USER_EMAIL as string);
  const password = useInput(process.env.REACT_APP_AWS_USER_PASS as string);
  const confirmPassword = useInput("");
  const code = useInput("");
  const [searchKey, setSearchKey] = useState<"student" | "host">("student");
  const studentOrHost = useRadiobox("whoisit", searchKey);
  // Switchers states
  const [loginWrong, setLoginWrong] = useState(EMPTY);
  const [disabledSendButton, setDisabledSendButton] = useState(true);

  const [createdNewUser, setCreatedNewUser] = useState(false);
  const [createdNewPass, setCreatedNewPass] = useState(0);

  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassError, setShowPassError] = useState(false);
  const [loading, setLoading] = useState(false);
  const seo = t("seo", { returnObjects: true }) as ISeo;

  // Front-end validators
  const emailValidationStatus = validateEmail({ line: email.value });
  const passwordValidationStatus = validateLength({
    line: password.value,
    params: { min: 8, max: 20 }
  });

  useEffect(() => {
    logOut(false);
  }, []);

  useEffect(() => {
    search.slice(3) &&
      setSearchKey(search.slice(3) === "h" ? "host" : "student");
  }, [pathname]);

  useEffect(() => {
    searchKey && studentOrHost.valueChange(searchKey);
  }, [searchKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDisabledSendButton(
      [
        emailValidationStatus.message === EMPTY,
        !emailValidationStatus.state,
        passwordValidationStatus.message === EMPTY,
        !passwordValidationStatus.state
      ].some((item) => item)
    );
  });

  const errorMessage = useMemo(() => {
    if (
      (loginWrong === INCORRECT_LOGIN_ERROR_MESSAGE &&
        i18n.language === "nl") ||
      (loginWrong === "E-mailadres en wachtwoord komen niet overeen." &&
        i18n.language === "gb")
    ) {
      return t("error.userNotAuthorized");
    }
    return loginWrong;
  }, [loginWrong, i18n.language]);

  const handleLogin = async () => {
    if (loading || isLoadingCurrentUser) {
      return;
    }

    setLoading(true);
    clearLocalStorage();
    signIn(email.value as string, password.value as string)
      .then((cognitoUser) => {
        window.dataLayer.push({
          event: "login",
          user_id: cognitoUser.id,
          user_type: cognitoUser.role
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        const errMessage =
          password.value === "" || password.value === undefined
            ? INCORRECT_LOGIN_ERROR_MESSAGE
            : (err.message as string);
        setLoginWrong(errMessage);
      });
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !disabledSendButton) {
      handleLogin();
    }
  };

  if (createdNewUser) {
    return (
      <ContainerLogin>
        <ContainerRedirect>
          <Title m="0 0 24px 0">{t("message.title")}</Title>
          <Label lineHeight="150%" p="0">
            {t("message.text")}
          </Label>
        </ContainerRedirect>
        <Button
          main
          fontWeight="400"
          w="100%"
          m="24px 0 0 0"
          onClick={() => {
            setCreatedNewUser((prev) => !prev);
            navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
          }}
        >
          {t("message.buttonTitle")}
        </Button>
      </ContainerLogin>
    );
  }

  // When changed password show info message
  if (createdNewPass === 2 || createdNewPass === 4) {
    return (
      <ContainerLogin>
        <ContainerRedirect>
          <Title m="0 0 24px 0">
            {createdNewPass === 2
              ? t("message.titleForgotPass")
              : t("message.titleSuccessChangePass")}
          </Title>
          <Label lineHeight="150%" p="0">
            {createdNewPass === 2
              ? t("message.textForgotPass")
              : t("message.textChangedPass")}
          </Label>
        </ContainerRedirect>
        <Button
          main
          fontWeight="400"
          w="100%"
          m="24px 0 0 0"
          onClick={() => {
            createdNewPass === 4 &&
              navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
            setCreatedNewPass(createdNewPass === 4 ? 0 : 3);
          }}
        >
          {t("message.buttonTitleForgotPass")}
        </Button>
      </ContainerLogin>
    );
  }

  // When changed password logics
  if (createdNewPass > 0) {
    const handleResetPass = () => {
      if (createdNewPass === 1) {
        forgotPassword(email.value as string).catch((err) => {
          setLoginWrong(err.message);
        });
      } else if (createdNewPass === 3) {
        if (password.value !== confirmPassword.value) {
          setLoginWrong(t("error.unequalPasswords"));
        } else {
          forgotPasswordSubmit(
            email.value as string,
            code.value as string,
            password.value as string
          )
            .then(() => {
              setCreatedNewPass(4);
              setLoginWrong(EMPTY);
            })
            .catch((err) => {
              setLoginWrong(err.message);
            });
        }
      }
    };
    return (
      <ContainerLogin>
        <Title m="0 0 24px 0">
          {t(
            `login.${createdNewPass === 1 ? "titleReset" : "titleResetNewPass"}`
          )}
        </Title>
        {createdNewPass === 1 && (
          <>
            <TextMessage style={{ wordBreak: "break-word" }}>
              {t("login.textReset")}
            </TextMessage>
            <InputLabel
              {...email}
              title={t("login.email")}
              type="email"
              placeholder={t("login.placeholderemail")}
              p="24px 0 0 0"
              onBlur={() => {
                setShowEmailError(true);
              }}
              onFocus={() => {
                setShowEmailError(false);
              }}
              error={!emailValidationStatus.state && showEmailError}
            >
              <ErrorMessage
                error={!emailValidationStatus.state && showEmailError}
              >
                {t("error.wrongEmail")}
              </ErrorMessage>
            </InputLabel>
          </>
        )}
        {createdNewPass === 3 && (
          <>
            <TextMessage style={{ wordBreak: "break-word" }}>
              {t("login.textReset2")}
            </TextMessage>
            <InputLabel
              {...code}
              title={t("login.code.title")}
              type="text"
              placeholder={t("login.code.placeholder")}
              p="24px 0 24px 0"
            />
            <InputLabelPassword
              {...password}
              title={t("edit.newPassword")}
              type="password"
              placeholder="********"
              onBlur={() => {
                setShowPassError(true);
              }}
              onFocus={() => {
                setShowPassError(false);
              }}
              error={!passwordValidationStatus.state && showPassError}
            >
              <ErrorMessage
                error={!passwordValidationStatus.state && showPassError}
              >
                {t("error.wrongPassword")}
              </ErrorMessage>
            </InputLabelPassword>
            <InputLabelPassword
              {...confirmPassword}
              title={t("edit.confirmPassword")}
              type="password"
              placeholder="********"
              p="0 0 24px 0"
            />
          </>
        )}
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgBody : "red"}
          fontWeight="400"
        >
          {errorMessage}
        </Label>
        <Button
          main
          fontWeight="400"
          onClick={() => {
            handleResetPass();
            createdNewPass === 4 &&
              navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
            setCreatedNewPass(createdNewPass === 4 ? 0 : 3);
          }}
          w="100%"
          disabled={
            createdNewPass === 1
              ? !emailValidationStatus.state || !email.value
              : password.value !== confirmPassword.value ||
                String(code.value).length === 0
          }
          m="24px 0 24px 0"
        >
          {t(
            `login.${
              createdNewPass === 1
                ? "buttonSendPassToEmail"
                : "buttonCraetePass"
            }`
          )}
        </Button>
        <SplitLine>
          <hr />
          <label>{t("login.splitline")}</label>
        </SplitLine>
        <StyledButton
          fontWeight="400"
          onClick={() => setCreatedNewPass(0)}
          w="100%"
          m="24px 0 0 0"
        >
          {t(`login.buttonFirst`)}
        </StyledButton>
      </ContainerLogin>
    );
  }

  return (
    <ContainerLogin>
      {seo && <HeaderSEO {...seo} />}
      {(loading || isLoadingCurrentUser) && (
        <LoaderCustom size={5} color={theme.colors.bgBody} />
      )}
      <Title m="0 0 24px 0">{t(`login.title`)}</Title>
      <form style={{ width: "100%" }}>
        <InputLabel
          {...email}
          title={t("login.email")}
          type="email"
          placeholder={t("login.placeholderemail")}
          onBlur={() => {
            setShowEmailError(true);
          }}
          onFocus={() => {
            setShowEmailError(false);
          }}
          error={!emailValidationStatus.state && showEmailError}
          onKeyPress={handleEnter}
        >
          <ErrorMessage error={!emailValidationStatus.state && showEmailError}>
            {t("error.wrongEmail")}
          </ErrorMessage>
        </InputLabel>
        <InputLabelPassword
          {...password}
          title={t("login.password")}
          type="password"
          placeholder="********"
          onBlur={() => {
            setShowPassError(true);
          }}
          onFocus={() => {
            setShowPassError(false);
          }}
          error={
            (!passwordValidationStatus.state ||
              password.value === "" ||
              password.value === undefined) &&
            showPassError
          }
          onKeyPress={handleEnter}
        >
          <ErrorMessage
            error={!passwordValidationStatus.state && showPassError}
          >
            {t("error.wrongPassword")}
          </ErrorMessage>
        </InputLabelPassword>
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgBody : "red"}
          fontWeight="400"
        >
          {errorMessage}
        </Label>
        <TextLink onClick={() => setCreatedNewPass(1)}>
          {t("login.forgotPass")}
        </TextLink>
      </form>

      <StyledButton
        fontWeight="400"
        onClick={handleLogin}
        w="100%"
        m="24px 0 24px 0"
      >
        {t(`login.buttonFirst`)}
      </StyledButton>
      <SplitLine>
        <hr />
        <label>{t("login.splitline")}</label>
      </SplitLine>
      <StyledButton
        outline
        fontWeight="400"
        onClick={(): void => {
          navigate(REGISTER_ROUTE);
        }}
        w="100%"
        m="24px 0 0 0"
      >
        {t(`login.buttonSecond`)}
      </StyledButton>
    </ContainerLogin>
  );
};
